<template>
    <div class="table-toolbar d-flex align-center">
        <TableSearchProductAutocomplete
            v-if="searchProductCompletion"
            ref="searchProductField"
            :placeholder="searchPlaceholder"
            :search-params="productSearchParams"
            :exclude-product-ids="excludeProductIds"
            :autofocus="autofocus"
            @product-select="$emit('product-select', $event)"
        />
        <v-text-field
            v-else-if="hasSearch"
            ref="searchField"
            :value="searchValue"
            class="table-toolbar__search flex-grow-1"
            :class="{ 'table-toolbar__search--filled': isSearchFilled }"
            dense
            outlined
            hide-details
            single-line
            clearable
            prepend-inner-icon="mdi-magnify"
            :placeholder="searchPlaceholder"
            :aria-label="searchPlaceholder"
            :autofocus="autofocus"
            @input="onSearchInput"
        />
        <div class="table-toolbar__actions d-flex align-center flex-shrink-0 ml-2">
            <v-tooltip v-if="hasFilters" bottom>
                <template v-slot:activator="{ on, attrs }">
                    <button
                        type="button"
                        class="table-toolbar__action"
                        :class="{ 'table-toolbar__action--active': filtersPanelOpen }"
                        v-bind="attrs"
                        v-on="on"
                        :aria-label="$translate('toggleFilters')"
                        :aria-pressed="filtersPanelOpen"
                        @click="$emit('toggle-filters')"
                    >
                        <v-icon small>mdi-tune-variant</v-icon>
                        <span
                            v-if="hasActiveFilters"
                            class="table-toolbar__badge-dot"
                            aria-hidden="true"
                        />
                    </button>
                </template>
                <span>{{
                    filtersPanelOpen
                        ? $translate("hideFilters")
                        : $translate("toggleFilters")
                }}</span>
            </v-tooltip>

            <TableViewsMenu
                ref="viewsMenu"
                v-if="viewsScope"
                :views-scope="viewsScope"
                :filters="filters"
                :views-exclude-keys="viewsExcludeKeys"
                :active="viewsMenuOpen"
                :has-active-view="hasActiveView"
                @toggle="$emit('toggle-views')"
                @apply="$emit('view-apply', $event)"
                @reset="$emit('view-reset')"
                @active-view-change="$emit('active-view-change', $event)"
            />

            <TableColumnsMenu
                v-if="enableColumnManagement && columns.length"
                :columns="columns"
                :column-state="columnState"
                :active="columnsMenuOpen"
                :customized="columnsCustomized"
                @toggle="$emit('toggle-columns')"
                @change="$emit('columns-change', $event)"
                @reset="$emit('columns-reset')"
            />
        </div>
    </div>
</template>

<script>
import TableViewsMenu from "./table-views-menu";
import TableColumnsMenu from "./table-columns-menu";
import TableSearchProductAutocomplete from "./table-search-product-autocomplete";

export default {
    name: "TableToolbar",
    components: { TableViewsMenu, TableColumnsMenu, TableSearchProductAutocomplete },
    props: {
        searchValue: {
            type: String,
            default: "",
        },
        searchPlaceholder: {
            type: String,
            default: "",
        },
        hasSearch: {
            type: Boolean,
            default: true,
        },
        hasFilters: {
            type: Boolean,
            default: true,
        },
        hasActiveFilters: {
            type: Boolean,
            default: false,
        },
        filtersPanelOpen: {
            type: Boolean,
            default: false,
        },
        viewsScope: {
            type: String,
            default: null,
        },
        viewsExcludeKeys: {
            type: Array,
            default: () => [],
        },
        filters: {
            type: Object,
            default: () => ({}),
        },
        viewsMenuOpen: {
            type: Boolean,
            default: false,
        },
        hasActiveView: {
            type: Boolean,
            default: false,
        },
        columnsMenuOpen: {
            type: Boolean,
            default: false,
        },
        enableColumnManagement: {
            type: Boolean,
            default: false,
        },
        columns: {
            type: Array,
            default: () => [],
        },
        columnState: {
            type: Array,
            default: () => [],
        },
        columnsCustomized: {
            type: Boolean,
            default: false,
        },
        searchProductCompletion: {
            type: Boolean,
            default: false,
        },
        productSearchParams: {
            type: Object,
            default: () => ({}),
        },
        excludeProductIds: {
            type: Array,
            default: () => [],
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            didAutofocus: false,
        };
    },
    computed: {
        isSearchFilled() {
            return Boolean(this.searchValue && String(this.searchValue).trim());
        },
    },
    watch: {
        autofocus() {
            this.tryAutofocus();
        },
        hasSearch() {
            this.tryAutofocus();
        },
        searchProductCompletion() {
            this.tryAutofocus();
        },
    },
    mounted() {
        this.tryAutofocus();
    },
    methods: {
        tryAutofocus() {
            if (!this.autofocus || this.didAutofocus) {
                return;
            }
            this.$nextTick(() => {
                if (this.focusSearch()) {
                    this.didAutofocus = true;
                    return;
                }
                window.setTimeout(() => {
                    if (this.didAutofocus) {
                        return;
                    }
                    if (this.focusSearch()) {
                        this.didAutofocus = true;
                    }
                }, 150);
            });
        },
        focusSearch() {
            if (this.searchProductCompletion) {
                const autocomplete = this.$refs.searchProductField;
                if (autocomplete && typeof autocomplete.focus === "function") {
                    autocomplete.focus();
                    return true;
                }
                return false;
            }
            const field = this.$refs.searchField;
            if (!field) {
                return false;
            }
            if (typeof field.focus === "function") {
                field.focus();
                return true;
            }
            const input = field.$el && field.$el.querySelector("input");
            if (input) {
                input.focus();
                return true;
            }
            return false;
        },
        onSearchInput(value) {
            this.$emit("search-change", value || null);
        },
    },
    i18n: {
        messages: {
            en: {
                toggleFilters: "Show filters",
                hideFilters: "Hide filters",
            },
            fr: {
                toggleFilters: "Afficher les filtres",
                hideFilters: "Masquer les filtres",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables.scss";

.table-toolbar {
    gap: 8px;

    &__search {
        min-width: 0;

        ::v-deep > .v-input__control > .v-input__slot {
            align-items: center !important;
            min-height: 40px !important;
        }

        ::v-deep .v-input__prepend-inner,
        ::v-deep .v-input__append-inner {
            align-self: center !important;
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            padding-top: 0 !important;
            display: inline-flex !important;
            align-items: center !important;
        }

        ::v-deep .v-input__prepend-inner .v-input__icon,
        ::v-deep .v-input__append-inner .v-input__icon {
            height: 20px !important;
            width: 20px !important;
            min-width: 20px !important;
        }

        ::v-deep .v-input__prepend-inner .v-icon,
        ::v-deep .v-input__append-inner .v-icon {
            font-size: 20px !important;
            width: 20px !important;
            height: 20px !important;
            min-width: 20px !important;
            color: $color-gs-semibold !important;
        }

        &--filled {
            ::v-deep > .v-input__control > .v-input__slot {
                background: $color-primary-extralight;
            }

            &:not(.v-input--is-focused) ::v-deep fieldset {
                border-color: $color-primary-light !important;
            }

            ::v-deep input {
                color: $color-primary-dark;
            }

            ::v-deep .v-input__prepend-inner .v-icon,
            ::v-deep .v-input__append-inner .v-icon {
                color: $color-primary-dark !important;
            }
        }
    }

    &__actions {
        gap: 4px;
    }

    &__action {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border: none;
        border-radius: 8px;
        background: transparent;
        cursor: pointer;
        color: $color-gs-extrabold;
        transition: background 0.15s ease;

        &:hover {
            background: $color-gs-extralight;
        }

        &:focus-visible {
            outline: 2px solid $color-primary;
            outline-offset: 2px;
        }

        &--active {
            background: $color-gs-light;
        }
    }

    &__badge-dot {
        position: absolute;
        top: 4px;
        right: 4px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: $color-info;
        border: 1.5px solid $color-gs-white;
    }
}
</style>
